// Cookie-consent state for the public site. Non-essential trackers (the Meta
// Pixel — see lib/gallery/pixel.ts) must not run until the visitor opts in here.
// SSR-safe: every access is guarded, and a missing/blocked store reads as
// "not decided, no analytics" so nothing fires by default.

export interface ConsentState {
  /** Analytics & advertising cookies (Meta Pixel etc.). */
  analytics: boolean;
  /** Whether the visitor has made a choice (controls whether the banner shows). */
  decided: boolean;
  ts: number;
}

const KEY = "ph_cookie_consent";
const CHANGE_EVENT = "ph-consent-change";
const OPEN_EVENT = "ph-consent-open";

const DEFAULT: ConsentState = { analytics: false, decided: false, ts: 0 };

export const getConsent = (): ConsentState => {
  if (typeof window === "undefined") return DEFAULT;
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return DEFAULT;
    const parsed = JSON.parse(raw) as Partial<ConsentState>;
    return {
      analytics: parsed.analytics === true,
      decided: parsed.decided === true,
      ts: typeof parsed.ts === "number" ? parsed.ts : 0,
    };
  } catch {
    return DEFAULT;
  }
};

export const hasAnalyticsConsent = (): boolean => getConsent().analytics;

/** Record a choice and notify listeners (pixel loader, banner). */
export const setConsent = (analytics: boolean): void => {
  if (typeof window === "undefined") return;
  const next: ConsentState = { analytics, decided: true, ts: Date.now() };
  try {
    window.localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    /* private mode / blocked storage — still fire the event for this page */
  }
  try {
    window.dispatchEvent(new CustomEvent<ConsentState>(CHANGE_EVENT, { detail: next }));
  } catch {
    /* no-op */
  }
};

/** Ask the banner to reopen (e.g. from a footer "Cookie settings" link). */
export const openConsentSettings = (): void => {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new Event(OPEN_EVENT));
  } catch {
    /* no-op */
  }
};

export const onConsentChange = (cb: (s: ConsentState) => void): (() => void) => {
  if (typeof window === "undefined") return () => {};
  const handler = () => cb(getConsent());
  window.addEventListener(CHANGE_EVENT, handler);
  return () => window.removeEventListener(CHANGE_EVENT, handler);
};

export const onConsentOpen = (cb: () => void): (() => void) => {
  if (typeof window === "undefined") return () => {};
  window.addEventListener(OPEN_EVENT, cb);
  return () => window.removeEventListener(OPEN_EVENT, cb);
};
